import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { GripVertical } from 'lucide-react';
import { AGENDA_CATEGORIES, type PresetCategory, type PresetChip } from './constants';

interface PaletteChipProps {
  category: PresetCategory;
  chip: PresetChip;
}

function PaletteChip({ category, chip }: PaletteChipProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${category.key}:${chip.name}`,
    data: { type: 'palette', categoryKey: category.key, color: category.color, chip },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="flex items-center gap-2 rounded-md px-2 py-1.5 mb-1 cursor-grab active:cursor-grabbing select-none border border-transparent hover:border-gray-200"
      style={{ backgroundColor: `${category.color}1a`, opacity: isDragging ? 0.4 : 1 }}
      title="Drag onto a day"
    >
      <GripVertical className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: category.color }} />
      <span className="text-xs font-medium text-gray-700 truncate">{chip.name}</span>
      <span className="ml-auto text-[10px] text-gray-400 flex-shrink-0">{chip.duration}m</span>
    </div>
  );
}

export function AgendaPalette() {
  return (
    <div className="w-60 flex-shrink-0 border-r border-gray-200 overflow-y-auto bg-white">
      <div className="px-3 py-2 border-b border-gray-200 sticky top-0 bg-white z-10">
        <h3 className="text-sm font-semibold text-gray-800">Agenda Items</h3>
        <p className="text-[11px] text-gray-500">Drag onto a day, or click a day to add.</p>
      </div>
      <div className="p-3">
        {AGENDA_CATEGORIES.map((category) => (
          <div key={category.key} className="mb-4">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: category.color }} />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {category.label}
              </span>
            </div>
            {category.chips.map((chip) => (
              <PaletteChip key={chip.name} category={category} chip={chip} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
